import dayjs from 'dayjs';
import * as React from 'react';
import { useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { Breakpoint, Language } from '../../enums';
import { useFormatDate, useParsers } from '../../hooks';
import { Status, UseFormatDate, UseParsers } from '../../interfaces';
import { Badge } from '../badge/badge.component';
import { Button } from '../button/button.component';

import {
  BottomContainer, 
  Container,
  ContentSection,
  ImageWithBoxTitle,
  ImageElement,
  InnerContainer,
  PriceItem,
  Prices,
  TopSection,
  StyledBoxTitle,
  BadgesContainer,
  AuctionDeadline,
  Price,
  InnerPriceItem
} from './artworkBiddingBox.styled';
import { Action, ArtworkBiddingBoxProps } from './artworkBiddingBox.types';

export const ArtworkBiddingBox: React.FC<ArtworkBiddingBoxProps> = (props: ArtworkBiddingBoxProps): JSX.Element => {
  const { badges, artworkDetails, className, price, maxPrice, image, actions, endDate, isAuctionFinished }: ArtworkBiddingBoxProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { formatDate, isReady }: UseFormatDate = useFormatDate(Language.Pl);
  const { parsePrice }: UseParsers = useParsers();
  const isMobile: boolean = useMediaQuery({ query: Breakpoint.Mobile });
  const currency: string = 'zł';
  const auctionDeadline: dayjs.Dayjs | null = useMemo(
    (): dayjs.Dayjs | null => {
      if (isReady) {
        return dayjs(formatDate(endDate, 'YYYY-MM-DD HH:mm:ss'));
      } else {
        return null;
      }
    },
    [endDate, isReady]
  );
  const todaysDate: dayjs.Dayjs = dayjs();
  const differenceInDays: number | undefined = auctionDeadline?.diff(todaysDate, 'day');
  const buttonData: Action | undefined = useMemo(
    (): Action | undefined => {
      if (!actions) {
        return;
      }

      if (actions?.bid) {
        return {
          label: 'onarte.ui.artworkBiddingBox.bid',
          action: actions.bid,
        };
      } else if (actions?.completeOrderDetails) {
        return {
          label: 'onarte.ui.artworkBiddingBox.completeOrderDetails',
          action: actions.completeOrderDetails,
        };
      } else if (actions?.orderDetails) {
        return {
          label: 'onarte.ui.artworkBiddingBox.orderDetails',
          action: actions.orderDetails
        };
      } else if (actions?.showAuction) {
        return {
          label: 'onarte.ui.artworkBiddingBox.showAuction',
          action: actions.showAuction
        };
      }
    },
    [actions]
  );

  return (
    <Container className={className}>
      <InnerContainer>
        <TopSection>
          <BadgesContainer>
            {badges.map((badge: Status): JSX.Element => (
              <Badge
                key={badge.label}
                color={badge.color}
                background={badge.background}
                border={badge.border}
                label={badge.label}
              />
            ))}
          </BadgesContainer>
          <AuctionDeadline>
            {`
              ${t(`onarte.ui.artworkBiddingBox.${isAuctionFinished  ? 'finishedAuction' : 'remainingDays'}`)}
              ${!isAuctionFinished ? differenceInDays ?? 0 : ''}
              (${formatDate(endDate, 'dd, DD MMM YYYY, HH:mm:ss')})
            `}
          </AuctionDeadline>
        </TopSection>
        <ContentSection $hasPaddingBottom={!!actions || isMobile}>
          <ImageWithBoxTitle>
            <ImageElement src={image} alt={artworkDetails.itemDescriptiveAttributes?.[0] ?? artworkDetails.itemName ?? ''} />
            <StyledBoxTitle boxTitleDetails={artworkDetails} />
          </ImageWithBoxTitle>
          {!isMobile && (
            <Prices>
              <Price $withBoldedFont>
                <PriceItem>
                  {t('onarte.ui.artworkBiddingBox.price')}
                  <InnerPriceItem>{parsePrice(price)} {currency}</InnerPriceItem>
                </PriceItem>
              </Price>
              <Price>
                <PriceItem>
                  {t('onarte.ui.artworkBiddingBox.maxPrice')}
                  <InnerPriceItem>{parsePrice(maxPrice)} {currency}</InnerPriceItem>
                </PriceItem>
              </Price>
            </Prices>
          )}
        </ContentSection>
        {(!!buttonData || isMobile) && (
          <BottomContainer>
            {isMobile && (
              <Prices $withMarginBottom={!!actions}>
                <Price $withBoldedFont>
                  <PriceItem>
                    {t('onarte.ui.artworkBiddingBox.price')}
                  </PriceItem>
                  <PriceItem>{parsePrice(price)} {currency}</PriceItem>
                </Price>
                <Price>
                  <PriceItem>
                    {t('onarte.ui.artworkBiddingBox.maxPrice')}
                  </PriceItem>
                  <PriceItem>{parsePrice(maxPrice)} {currency}</PriceItem>
                </Price>
              </Prices>
            )}
            {buttonData && (
              <Button
                data-testid='button'
                fullWidth={isMobile}
                label={t(buttonData.label)}
                onClick={buttonData.action}
              />
            )}
          </BottomContainer>
        )}
      </InnerContainer>
    </Container>
  );
};
